import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { Download, X } from 'react-feather';
import appQuizApi from '../../../api/appQuiz';

const ExportModal = ({ isOpen, toggle, onExport, currentFilters, exporting }) => {
  const [exportOptions, setExportOptions] = useState({
    includeAttempts: true,
    quizId: '',
    minPoints: '',
    filename: ''
  });

  // State for quiz options
  const [quizOptions, setQuizOptions] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);

  // Load quiz options when modal opens
  useEffect(() => {
    if (isOpen) {
      loadQuizOptions();
    }
  }, [isOpen]);

  const loadQuizOptions = async () => {
    try {
      setLoadingQuizzes(true);
      const response = await appQuizApi.getAllQuizTemplates({ limit: 500 });
      
      // Handle different API response structures
      const quizzes = 
        response?.data?.quizzes || 
        response?.data?.quizTemplates || 
        response?.quizzes || 
        response?.quizTemplates || 
        response?.data || 
        response || 
        [];

      const options = Array.isArray(quizzes)
        ? quizzes
            .map((quiz) => {
              const id = quiz.id || quiz.quizId;
              if (!id) return null;
              const name = quiz.name || quiz.title || `Quiz #${id}`;
              return { id, name };
            })
            .filter(Boolean)
            .sort((a, b) => a.id - b.id) // Sort by ID
        : [];

      setQuizOptions(options);
    } catch (error) {
      console.error('Error loading quiz options:', error);
      setQuizOptions([]);
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const handleChange = (key, value) => {
    setExportOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleExport = () => {
    const params = {
      includeAttempts: exportOptions.includeAttempts,
    };

    // Only add quizId if it's selected and not empty
    if (exportOptions.quizId && exportOptions.quizId !== '') {
      params.quizId = parseInt(exportOptions.quizId);
    }

    if (exportOptions.minPoints) {
      params.minPoints = parseInt(exportOptions.minPoints);
    }

    if (currentFilters.role_code) {
      params.role_code = currentFilters.role_code;
    }

    if (exportOptions.filename) {
      params.filename = exportOptions.filename;
    }

    onExport(params);
  };

  const handleReset = () => {
    setExportOptions({
      includeAttempts: true,
      quizId: '',
      minPoints: '',
      filename: ''
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        <Download size={20} className="me-2" />
        Export Users to Excel
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <div className="form-check form-switch">
              <Input
                type="checkbox"
                className="form-check-input"
                id="includeAttempts"
                checked={exportOptions.includeAttempts}
                onChange={(e) => handleChange('includeAttempts', e.target.checked)}
              />
              <Label className="form-check-label" for="includeAttempts">
                Bao gồm dữ liệu attempts
              </Label>
            </div>
          </FormGroup>

          <FormGroup>
            <Label for="quizId">Quiz (tùy chọn)</Label>
            <Input
              type="select"
              id="quizId"
              value={exportOptions.quizId}
              onChange={(e) => handleChange('quizId', e.target.value)}
              disabled={loadingQuizzes}
            >
              <option value="">Tất cả quiz</option>
              {quizOptions.map((quiz) => (
                <option key={quiz.id} value={quiz.id}>
                  {quiz.name}
                </option>
              ))}
            </Input>
            {loadingQuizzes && (
              <small className="text-muted">Đang tải danh sách quiz...</small>
            )}
            {!loadingQuizzes && quizOptions.length === 0 && (
              <small className="text-muted">Không có quiz nào</small>
            )}
            {!loadingQuizzes && quizOptions.length > 0 && (
              <small className="text-muted">Chỉ export users đã làm quiz được chọn</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="minPoints">Điểm tối thiểu (tùy chọn)</Label>
            <Input
              type="number"
              id="minPoints"
              placeholder="VD: 50"
              value={exportOptions.minPoints}
              onChange={(e) => handleChange('minPoints', e.target.value)}
            />
            <small className="text-muted">Chỉ export users có điểm &gt;= giá trị này</small>
          </FormGroup>

          {currentFilters.role_code && (
            <div className="alert alert-info mb-3">
              <small>
                <strong>Lưu ý:</strong> Export sẽ áp dụng filter Role Code hiện tại: <strong>{currentFilters.role_code}</strong>
              </small>
            </div>
          )}

          <FormGroup>
            <Label for="filename">Tên file tùy chỉnh (tùy chọn)</Label>
            <Input
              type="text"
              id="filename"
              placeholder="Để trống để tự động tạo tên"
              value={exportOptions.filename}
              onChange={(e) => handleChange('filename', e.target.value)}
            />
            <small className="text-muted">Nếu không nhập, tên file sẽ được tạo tự động</small>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleReset} disabled={exporting}>
          <X size={14} className="me-1" />
          Reset
        </Button>
        <Button color="primary" onClick={handleExport} disabled={exporting}>
          <Download size={14} className="me-1" />
          {exporting ? 'Đang export...' : 'Export Excel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExportModal;